import { getUpgradePremiumOfferAction } from "./GetUpgradePremiumOfferAction";
import { processOffers, getOffersByDirection, resolveLegSourceCode } from "./UpgradePremiumHelper";
import { BaseAction } from "../../../core/actions/BaseAction";
import { getRecoilStateValue, updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { IBookedTrips, ITrip, IUpgradeExchangeOfferResponse } from "../../../Orchestrator/Types/Index";
import { ticketClassTypeEnum, TripTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { IDefaultActionResult } from "../../../Orchestrator/Types/Interface/ActionParams/DefaultActionResult";

export interface IProcessUpgradeOffersParams {
    trip: IBookedTrips;
    setHasUpgradeOffers: (hasOffers: boolean) => void;
    setManageBookingLoading?: (isLoading: boolean) => void;
}

class ProcessUpgradeOffersAction extends BaseAction<IDefaultActionResult, IProcessUpgradeOffersParams> {
    override async run(): Promise<IDefaultActionResult> {
        let actionResult: IDefaultActionResult = {
            data: null,
            message: "",
            status: "fail"
        };
        const {
            trip,
            setHasUpgradeOffers,
            setManageBookingLoading
        } = this.params!;

        // Resolve the leg's CURRENT booked product code (e.g. STV/SFF) so the
        // upgrade target follows its own tier (Value→PRV, Flex→PFF) instead of
        // the backend's generic RECOMMENDED tag. Requires a positive match
        // between the atom's booking and the trip being processed; otherwise it
        // returns undefined and processOffers keeps its prior RECOMMENDED-based
        // behaviour (no regression, no risk of reading a stale booking).
        const getCurrentSourceCode = (direction: TripTypeEnum): string | undefined => {
            const bookingResp: any = getRecoilStateValue("bookingResponseAtom");
            const b: any = bookingResp?.booking || bookingResp;
            if (!b?.id || !trip?.bookingId || b.id !== trip.bookingId) return undefined;
            const dirOffers = getOffersByDirection(b?.bookedOffers || [], b?.trips || [], direction);
            return resolveLegSourceCode(dirOffers);
        };

        try {
            setManageBookingLoading?.(true);
            setHasUpgradeOffers(false);

            const isUpgradeEligibilitySatisfied = (t: IBookedTrips) => {
                let isStandard = true;
                if (t?.inboundData === undefined && (!t?.inboundOfferID)) {
                    isStandard = t.selectedPriceClass === ticketClassTypeEnum.standardValue ||
                        t.selectedPriceClass === ticketClassTypeEnum.standardPro;
                }
                const noWheelchair = t.passengers?.every(p => !p.prmNeeds?.includes("WHEELCHAIR"));
                const bookingStatus = (t?.status == "REFUNDED" || t?.status == "RELEASED") ? false : true;
                return Boolean(isStandard && noWheelchair && bookingStatus);
            };

            if (!isUpgradeEligibilitySatisfied(trip)) {
                actionResult.status = "success";
                actionResult.data = { hasOffers: false };
                return actionResult;
            }

            const mapPassengerIdsToExternalRefs = (ids: string[] | undefined) => {
                if (!ids || !Array.isArray(ids) || !trip?.passengers) return [] as string[];
                const idToExternalRef = new Map<string, string>();
                trip.passengers.forEach((p: any) => {
                    if (p?.id && p?.externalRef) idToExternalRef.set(p.id, p.externalRef);
                });
                return ids.map((id) => idToExternalRef.get(id)).filter(Boolean) as string[];
            };

            const getPassengerIdsFromFulfillmentIds = (fulfillmentIds: string[], isInbound: boolean = false) => {
                if (!fulfillmentIds || !Array.isArray(fulfillmentIds)) {
                    return [];
                }
                const actualPassengerIds = trip?.passengers?.map((p: any) => p.id) || [];
                const passengerDataIds = isInbound 
                    ? (trip?.passengerData?.inbound || [])
                    : (trip?.passengerData?.outbound || []);
                
                const validPassengerIds = passengerDataIds.filter((id: string) => 
                    actualPassengerIds.includes(id)
                );
                
                if (validPassengerIds.length >= fulfillmentIds.length) {
                    return validPassengerIds.slice(0, fulfillmentIds.length);
                }
                
                return actualPassengerIds.slice(0, fulfillmentIds.length);
            };

            const outboundFulfillmentIds = trip?.fulfillmentIds || [];
            
            const outboundPassengerIds = getPassengerIdsFromFulfillmentIds(outboundFulfillmentIds);
            const outboundPassengerRefs = outboundPassengerIds.length > 0
                ? mapPassengerIdsToExternalRefs(outboundPassengerIds)
                : trip?.passengers?.map((p) => p.externalRef) || [];

            const isOutboundEligibleClass =
                trip?.selectedPriceClass === ticketClassTypeEnum.standardValue ||
                trip?.selectedPriceClass === ticketClassTypeEnum.standardPro;

            let outboundHasOffers = false;
            if (isOutboundEligibleClass) {
                getUpgradePremiumOfferAction.setParams({
                    fulfillmentIds: outboundFulfillmentIds,
                    passengers: outboundPassengerRefs,
                    bookingId: trip?.bookingId,
                    showLoader: true
                });
                await getUpgradePremiumOfferAction.execute();

                // Check if the action succeeded before processing results
                if (getUpgradePremiumOfferAction.result?.status === "success") {
                    const outboundOffers: IUpgradeExchangeOfferResponse = getUpgradePremiumOfferAction.result?.data;
                    if (outboundOffers && Array.isArray(outboundOffers.exchangeOffers)) {
                        const outboundSourceCode = getCurrentSourceCode(TripTypeEnum.OUT_BOUND);
                        const filteredOutboundOffers = (outboundOffers?.trips || [])
                            .map((trip: any) => processOffers(trip as ITrip, outboundOffers.exchangeOffers, outboundSourceCode))
                            .filter((res: any) => res && res.premiumOffers && res.premiumOffers.length > 0)
                            .flatMap((res: any) => res.premiumOffers);

                        const updatedOutboundOffers: IUpgradeExchangeOfferResponse = {
                            ...outboundOffers,
                            exchangeOffers: filteredOutboundOffers,
                        };
                        outboundHasOffers = Boolean(updatedOutboundOffers?.exchangeOffers?.length > 0);
                        updateRecoilState("upgradeOutboundOffersAtom", updatedOutboundOffers);
                    } else {
                        updateRecoilState("upgradeOutboundOffersAtom", outboundOffers);
                    }
                } else {
                    // API failed - clear offers to hide the upgrade button
                    updateRecoilState("upgradeOutboundOffersAtom", null);
                }
            }

            let inboundHasOffers = false;
            if (trip?.bookingType === "roundTrip" && trip?.inboundData && trip?.inboundOfferID) {
                const inboundFulfillmentIds = trip?.inboundData?.fulfillmentIds || [];
                
                const inboundPassengerIds = getPassengerIdsFromFulfillmentIds(inboundFulfillmentIds, true);
                const inboundPassengerRefs = inboundPassengerIds.length > 0
                    ? mapPassengerIdsToExternalRefs(inboundPassengerIds)
                    : trip?.passengers?.map((p) => p.externalRef) || [];

                const inboundSelectedClass = trip?.inboundData?.selectedPriceClass;
                const isInboundEligibleClass =
                    inboundSelectedClass === ticketClassTypeEnum.standardValue ||
                    inboundSelectedClass === ticketClassTypeEnum.standardPro;
                if (isInboundEligibleClass) {
                    getUpgradePremiumOfferAction.setParams({
                        fulfillmentIds: inboundFulfillmentIds,
                        passengers: inboundPassengerRefs,
                        bookingId: trip?.bookingId,
                        showLoader: true
                    });
                    await getUpgradePremiumOfferAction.execute();

                    // Check if the action succeeded before processing results
                    if (getUpgradePremiumOfferAction.result?.status === "success") {
                        const inboundOffers: IUpgradeExchangeOfferResponse = getUpgradePremiumOfferAction.result?.data;
                        if (inboundOffers && Array.isArray(inboundOffers.exchangeOffers)) {
                            const inboundSourceCode = getCurrentSourceCode(TripTypeEnum.IN_BOUND);
                            const filteredInboundOffers = (inboundOffers?.trips || [])
                                .map((trip: any) => processOffers(trip as ITrip, inboundOffers.exchangeOffers, inboundSourceCode))
                                .filter((res: any) => res && res.premiumOffers && res.premiumOffers.length > 0)
                                .flatMap((res: any) => res.premiumOffers);

                            const updatedInboundOffers: IUpgradeExchangeOfferResponse = {
                                ...inboundOffers,
                                exchangeOffers: filteredInboundOffers,
                            };
                            inboundHasOffers = Boolean(updatedInboundOffers?.exchangeOffers?.length > 0);
                            updateRecoilState("upgradeInboundOffersAtom", updatedInboundOffers);
                        } else {
                            updateRecoilState("upgradeInboundOffersAtom", inboundOffers);
                        }
                    } else {
                        // API failed - clear offers to hide the upgrade button
                        updateRecoilState("upgradeInboundOffersAtom", null);
                    }
                }
            }

            const hasOffers = outboundHasOffers || inboundHasOffers;
            setHasUpgradeOffers(hasOffers);

            actionResult.status = "success";
            actionResult.data = { hasOffers };
        } catch (error) {
            console.error("Error in ProcessUpgradeOffersAction:", error);
            actionResult.message = "Something went wrong";
        } finally {
            setManageBookingLoading?.(false);
        }

        return actionResult;
    }
}

export const processUpgradeOffersAction = new ProcessUpgradeOffersAction();
